/* ===========================================================
   pages/NotesDrive.jsx — Act 3 (his asks, 2026-10-28)

   Notes Protocol (notes.weatherprotocol.org, or /notes): small notes
   like Google Keep, in the Weather Protocol look. "Take a note…" at
   the top, cards in columns, colors, pins, checklists, archive and
   trash, and your docs listed on the left. Notes are their own small
   records (sync/notes.js), not docs. NotesSide shows them on the right
   of the docs list and of Drive (his ask), not inside a doc.

   Drive Protocol (drive.weatherprotocol.org, or /drive): everything
   you have in one place (groups, docs, notes) plus files you upload
   (a doc of kind 'file' holding the file). Same 200 MB as Docs.
   =========================================================== */

import { useRef, useState, useEffect } from 'preact/hooks';
import { canSync, syncedFolder, pickFolder, syncOnce } from '../files/folder-sync.js';
import { docs, session, online } from '../state.js';
import { go } from '../lib/router.js';
import { api } from '../lib/api.js';
import { openApp, appHref } from '../lib/hosts.js';
import { createDoc, flushOutbox, groups, trashDoc, refreshList } from '../sync/docs.js';
import { notes, saveNote, patchNote, deleteNoteForever, refreshNotes, blankNote, isEmpty, useNotesBoot } from '../sync/notes.js';
import { Icon, IconButton, Button, timeAgo, toast, confirmDialog } from '../ui/ui.jsx';
import { Proto } from '../ui/proto.jsx';
import { TopBar } from './Home.jsx';

const live = () => docs.value.filter(d => !d.deletedAt && !d.local);
const docsOnly = () => live().filter(d => d.kind === 'doc' || d.kind === 'copy' || d.kind === 'note');

const COLORS = [['', 'Default'], ['red', 'Red'], ['orange', 'Orange'], ['yellow', 'Yellow'], ['green', 'Green'],
  ['teal', 'Teal'], ['blue', 'Blue'], ['purple', 'Purple'], ['pink', 'Pink'], ['gray', 'Gray']];

const textOf = n => `${n.title}\n${n.body}\n${(n.items || []).map(i => i.t).join('\n')}`.toLowerCase();

/** Lines ↔ checklist. */
const toItems = n => ({ ...n, items: (n.body || '').split('\n').filter(l => l.trim()).map(t => ({ t, done: false })).concat(n.body.trim() ? [] : [{ t: '', done: false }]), body: '' });
const toText = n => ({ ...n, body: (n.items || []).map(i => i.t).filter(Boolean).join('\n'), items: null });
const toggleList = n => (n.items ? toText(n) : toItems(n));

function grow(el) { if (el) { el.style.height = 'auto'; el.style.height = `${el.scrollHeight}px`; } }

/* ---------- one note's writing (used by the composer and the open note) ---------- */

function NoteFields({ note, onChange, autoFocus }) {
  const box = useRef();
  const set = patch => onChange({ ...note, ...patch });
  const focusItem = i => setTimeout(() => box.current?.querySelectorAll('.kitem input[type=text]')[i]?.focus(), 0);
  useEffect(() => { box.current?.querySelectorAll('textarea').forEach(grow); });
  useEffect(() => {
    if (!autoFocus) return;
    const f = box.current?.querySelector('textarea') || [...(box.current?.querySelectorAll('.kitem input[type=text]') || [])].pop();
    f?.focus();
  }, []);
  const items = note.items;
  const setItem = (i, patch) => set({ items: items.map((it, j) => (j === i ? { ...it, ...patch } : it)) });
  return (
    <div class="kfields" ref={box}>
      <input class="ktitle" type="text" placeholder="Title" value={note.title} maxLength={200}
        onInput={e => set({ title: e.target.value })} />
      {!items ? (
        <textarea class="kbody" placeholder="Take a note…" value={note.body} rows={1} autoFocus={autoFocus}
          onInput={e => { grow(e.target); set({ body: e.target.value }); }} />
      ) : (
        <ul class="kitems">
          {items.map((it, i) => (
            <li class={`kitem ${it.done ? 'done' : ''}`} key={i}>
              <input type="checkbox" checked={it.done} aria-label="Done" onChange={() => setItem(i, { done: !it.done })} />
              <input type="text" value={it.t} placeholder="List item" autoFocus={autoFocus && i === items.length - 1}
                onInput={e => setItem(i, { t: e.target.value })}
                onKeyDown={e => {
                  if (e.key === 'Enter') { e.preventDefault(); set({ items: [...items.slice(0, i + 1), { t: '', done: false }, ...items.slice(i + 1)] }); focusItem(i + 1); }
                  else if (e.key === 'Backspace' && !it.t && items.length > 1) { e.preventDefault(); set({ items: items.filter((_, j) => j !== i) }); focusItem(Math.max(0, i - 1)); }
                }} />
              <IconButton icon="close" label="Remove item" size={14} class="kitem-x" onClick={() => set({ items: items.filter((_, j) => j !== i) })} />
            </li>
          ))}
          <li class="kitem add"><button type="button" onClick={() => { set({ items: [...items, { t: '', done: false }] }); focusItem(items.length); }}><Icon name="plus" size={15} /> List item</button></li>
        </ul>
      )}
    </div>
  );
}

function ColorDots({ value, onPick }) {
  return (
    <div class="kcolors" role="group" aria-label="Note color">
      {COLORS.map(([c, label]) => (
        <button type="button" key={c || 'none'} class={`kdot kc-${c || 'none'} ${value === c ? 'on' : ''}`} title={label} aria-label={label}
          onClick={e => { e.stopPropagation(); onPick(c); }} />
      ))}
    </div>
  );
}

/** The little row of buttons under a note. */
function NoteTools({ note, onChange, onDone, inTrash }) {
  const [colors, setColors] = useState(false);
  if (inTrash) {
    return (
      <div class="ktools" onClick={e => e.stopPropagation()}>
        <IconButton icon="restore" label="Restore" size={16} onClick={() => onChange({ ...note, deletedAt: null })} />
        <IconButton icon="trash" label="Delete forever" size={16} onClick={async () => {
          if (await confirmDialog({ title: 'Delete this note forever?', text: 'This can\'t be undone.', ok: 'Delete forever', danger: true })) deleteNoteForever(note.id);
        }} />
        <span class="grow" />
        {onDone && <Button size="sm" kind="ghost" onClick={onDone}>Close</Button>}
      </div>
    );
  }
  return (
    <div class="ktools" onClick={e => e.stopPropagation()}>
      <IconButton icon="star" label={note.pinned ? 'Unpin' : 'Pin to the top'} active={note.pinned} size={16} onClick={() => onChange({ ...note, pinned: !note.pinned })} />
      <IconButton icon="highlight" label="Color" active={colors} size={16} onClick={() => setColors(!colors)} />
      <IconButton icon="checklist" label={note.items ? 'Plain text' : 'Checklist'} size={16} onClick={() => onChange(toggleList(note))} />
      <IconButton icon="download" label={note.archived ? 'Take out of archive' : 'Archive'} active={note.archived} size={16} onClick={() => { onChange({ ...note, archived: !note.archived, pinned: false }); onDone?.(); }} />
      <IconButton icon="trash" label="Move to trash" size={16} onClick={() => { onChange({ ...note, deletedAt: Date.now(), pinned: false }); onDone?.(); }} />
      <span class="grow" />
      {onDone && <Button size="sm" kind="ghost" onClick={onDone}>Close</Button>}
      {colors && <ColorDots value={note.color} onPick={c => { onChange({ ...note, color: c }); setColors(false); }} />}
    </div>
  );
}

/* ---------- "Take a note…" ---------- */

function Composer({ compact }) {
  const [draft, setDraft] = useState(null);
  const wrap = useRef();
  const close = () => {
    setDraft(d => { if (d && !isEmpty(d)) saveNote(d); return null; });
  };
  useEffect(() => {
    if (!draft) return;
    const away = e => { if (!wrap.current?.contains(e.target) && !e.target.closest?.('.menu, .dialog')) close(); };
    const esc = e => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', esc); };
  }, [!!draft]);
  if (!draft) {
    return (
      <div class={`kcompose closed ${compact ? 'compact' : ''}`} ref={wrap}>
        <button type="button" class="kcompose-open" onClick={() => setDraft(blankNote())}>Take a note…</button>
        <IconButton icon="checklist" label="New checklist" onClick={() => setDraft(blankNote({ items: [{ t: '', done: false }] }))} />
      </div>
    );
  }
  return (
    <div class={`kcompose kcard kc-${draft.color || 'none'} ${compact ? 'compact' : ''}`} ref={wrap}>
      <NoteFields note={draft} onChange={setDraft} autoFocus />
      <NoteTools note={draft} onChange={n => (n.deletedAt ? setDraft(null) : setDraft(n))} onDone={close} />
    </div>
  );
}

/* ---------- cards ---------- */

function NoteCard({ note, onOpen, inTrash }) {
  const items = note.items || [];
  const shown = items.filter(i => !i.done).concat(items.filter(i => i.done)).slice(0, 8);
  return (
    <article class={`kcard kc-${note.color || 'none'} ${note.pinned ? 'pinned' : ''}`} tabIndex={0} aria-label={note.title || 'Note'}
      onClick={() => onOpen(note.id)} onKeyDown={e => { if (e.key === 'Enter' && e.target === e.currentTarget) onOpen(note.id); }}>
      {note.pinned && <span class="kpin"><Icon name="star" size={14} /></span>}
      {note.title && <h3>{note.title}</h3>}
      {note.items ? (
        <ul class="kprev">
          {shown.map((it, i) => (
            <li key={i} class={it.done ? 'done' : ''}>
              <input type="checkbox" checked={it.done} aria-label={it.t || 'Item'} disabled={inTrash}
                onClick={e => e.stopPropagation()}
                onChange={() => patchNote(note.id, { items: items.map(x => (x === it ? { ...x, done: !x.done } : x)) })} />
              <span>{it.t}</span>
            </li>
          ))}
          {items.length > 8 && <li class="more">+{items.length - 8} more</li>}
        </ul>
      ) : note.body ? <p>{note.body}</p> : !note.title && <p class="faint">Empty note</p>}
      <NoteTools note={note} onChange={saveNote} inTrash={inTrash} />
    </article>
  );
}

/** A note opened big in the middle of the screen; every change saves. */
function OpenNote({ id, onClose }) {
  const note = notes.value.find(n => n.id === id);
  useEffect(() => {
    const esc = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, []);
  if (!note) return null;
  const inTrash = !!note.deletedAt;
  return (
    <div class="kmodal-back" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div class={`kmodal kcard kc-${note.color || 'none'}`} role="dialog" aria-label={note.title || 'Note'}>
        <NoteFields note={note} onChange={n => !inTrash && saveNote(n)} />
        <div class="kedited">Edited {timeAgo(note.updatedAt)}</div>
        <NoteTools note={note} onChange={saveNote} onDone={onClose} inTrash={inTrash} />
      </div>
    </div>
  );
}

function Masonry({ list, onOpen, inTrash }) {
  return <div class="kgrid">{list.map(n => <NoteCard note={n} key={n.id} onOpen={onOpen} inTrash={inTrash} />)}</div>;
}

function useNotesSync() {
  useNotesBoot();
  useEffect(() => {
    refreshNotes();
    const on = () => refreshNotes();
    const vis = () => { if (document.visibilityState === 'visible') refreshNotes(); };
    window.addEventListener('online', on);
    document.addEventListener('visibilitychange', vis);
    return () => { window.removeEventListener('online', on); document.removeEventListener('visibilitychange', vis); };
  }, []);
}

/* ---------- Notes ---------- */

const VIEWS = [['notes', 'Notes', 'pencil'], ['archive', 'Archive', 'download'], ['trash', 'Trash', 'trash']];

export function NotesPage() {
  useNotesSync();
  const [view, setView] = useState('notes');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(null);
  const [showDocs, setShowDocs] = useState(false);
  const all = notes.value;
  const find = q.trim().toLowerCase();
  const list = all.filter(n => (view === 'trash' ? n.deletedAt : !n.deletedAt && (view === 'archive' ? n.archived : !n.archived)))
    .filter(n => !find || textOf(n).includes(find));
  const pinned = view === 'notes' ? list.filter(n => n.pinned) : [];
  const others = view === 'notes' ? list.filter(n => !n.pinned) : list;
  const myDocs = docsOnly();
  return (
    <div class="home keep">
      <TopBar title="Notes" />
      <div class="keep-body">
        <aside class={`keep-side ${showDocs ? 'show-docs' : ''}`} aria-label="Notes and docs">
          <nav class="keep-views">
            {VIEWS.map(([v, label, icon]) => (
              <button type="button" key={v} class={`keep-view ${view === v ? 'on' : ''}`} aria-current={view === v ? 'page' : undefined} onClick={() => setView(v)}>
                <Icon name={icon} size={17} /><span>{label}</span>
              </button>
            ))}
            <button type="button" class="keep-view docs-toggle" onClick={() => setShowDocs(!showDocs)}><Icon name="doc" size={17} /><span>Docs</span></button>
          </nav>
          <div class="keep-docs">
            <div class="keep-docs-head">
              <h2>Your docs</h2>
              <IconButton icon="plus" label="New doc" size={16} onClick={async () => openApp('docs', `/d/${await createDoc()}`)} />
            </div>
            {myDocs.length ? (
              <ul>
                {myDocs.slice(0, 60).map(d => (
                  <li key={d.id}>
                    <a href={appHref('docs', `/d/${d.id}`)} onClick={e => { e.preventDefault(); openApp('docs', `/d/${d.id}`); }} title={d.title || 'Untitled document'}>
                      <Icon name={d.kind === 'note' ? 'pencil' : 'doc'} size={15} /><span>{d.title || 'Untitled document'}</span>
                    </a>
                  </li>
                ))}
              </ul>
            ) : <p class="hint">No docs yet.</p>}
            <a class="keep-docs-all" href={appHref('docs')} onClick={e => { e.preventDefault(); openApp('docs'); }}>All docs →</a>
          </div>
        </aside>
        <main id="main" class="keep-main">
          <div class="keep-search">
            <Icon name="search" size={16} />
            <input type="search" placeholder="Search notes" value={q} onInput={e => setQ(e.target.value)} aria-label="Search notes" />
          </div>
          {view === 'notes' && <Composer />}
          {view === 'trash' && list.length > 0 && <p class="hint keep-hint">Notes in the trash are deleted after 7 days.</p>}
          {!list.length ? (
            <div class="empty keep-empty">
              <Proto size={64} />
              <p>{find ? 'No notes match that.' : view === 'notes' ? 'No notes yet. Quick things to remember go here.' : view === 'archive' ? 'Nothing archived. Archived notes wait here, out of the way.' : 'The trash is empty.'}</p>
            </div>
          ) : (
            <>
              {pinned.length > 0 && <><h2 class="keep-label">Pinned</h2><Masonry list={pinned} onOpen={setOpen} /></>}
              {pinned.length > 0 && others.length > 0 && <h2 class="keep-label">Others</h2>}
              <Masonry list={others} onOpen={setOpen} inTrash={view === 'trash'} />
            </>
          )}
          {!online.value && <p class="hint keep-hint">Offline: your notes save on this device and send when you're back.</p>}
        </main>
      </div>
      {open && <OpenNote id={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

const SIDE = 'dp:notesSide';
/** Notes on the right of the docs list and Drive; can be folded away. */
export function NotesSide() {
  const [shut, setShut] = useState(() => { try { return localStorage.getItem(SIDE) === '0'; } catch { return false; } });
  const flip = v => { setShut(v); try { localStorage.setItem(SIDE, v ? '0' : '1'); } catch { /* private */ } };
  if (shut) return <button type="button" class="notes-rail" onClick={() => flip(false)} aria-label="Show notes"><Icon name="pencil" size={17} /><span>Notes</span></button>;
  return <NotesPanel onClose={() => flip(true)} />;
}

function NotesPanel({ onClose }) {
  useNotesSync();
  const [open, setOpen] = useState(null);
  const list = notes.value.filter(n => !n.deletedAt && !n.archived);
  const sorted = list.filter(n => n.pinned).concat(list.filter(n => !n.pinned));
  return (
    <aside class="panel keep-panel" aria-label="Notes">
      <div class="panel-head"><h2>Notes</h2><IconButton icon="close" label="Hide notes" onClick={onClose} /></div>
      <div class="panel-body">
        <Composer compact />
        <div class="kgrid one">{sorted.map(n => <NoteCard note={n} key={n.id} onOpen={setOpen} />)}</div>
        {!sorted.length && <p class="hint">No notes yet.</p>}
        <a class="keep-docs-all" href={appHref('notes')} onClick={e => { e.preventDefault(); openApp('notes'); }}>Open Notes →</a>
      </div>
      {open && <OpenNote id={open} onClose={() => setOpen(null)} />}
    </aside>
  );
}

/* ---------- Drive ---------- */

const size = b => (b > 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1e3))} KB`);

async function upload(files) {
  for (const f of files) {
    if (f.size > 20 * 1024 * 1024) { toast(`${f.name} is over 20 MB, too big for one file.`); continue; }
    const id = await createDoc({ title: f.name, kind: 'file' });
    await flushOutbox();
    try {
      const r = await api(`/api/files?doc=${id}`, { method: 'POST', raw: f, type: 'application/octet-stream' });
      // the file's address rides in the snippet, its size in words
      await api(`/api/docs/${id}`, { method: 'PATCH', body: { snippet: r.url, words: r.bytes } });
      toast(`Uploaded ${f.name}.`);
    } catch (err) {
      toast(err, { kind: 'error' });
      trashDoc(id);
    }
  }
  refreshList();
}

export function DrivePage() {
  const input = useRef();
  const [over, setOver] = useState(false);
  const all = live();
  const files = all.filter(d => d.kind === 'file');
  const docsList = all.filter(d => d.kind === 'doc' || d.kind === 'copy' || d.kind === 'note');
  const used = session.value.usage?.used || 0;
  // a folder on this computer kept in step with Drive (files both ways, docs as .md)
  const [folder, setFolder] = useState(null);
  const [last, setLast] = useState('');
  useEffect(() => {
    if (!canSync()) return;
    let t = null, alive = true;
    const pass = async () => {
      try { const r = await syncOnce(); if (r && alive) setLast(`Synced ${new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}${r.up || r.down || r.docs ? ` · ${r.up} up, ${r.down} down, ${r.docs} docs` : ''}`); }
      catch (err) { if (alive) setLast(`Sync paused: ${err.message}`); }
      t = setTimeout(pass, 30_000);
    };
    syncedFolder().then(d => { if (!alive) return; setFolder(d || null); if (d) pass(); });
    return () => { alive = false; clearTimeout(t); };
  }, []);
  const row = (d, icon, open) => (
    <li class="docrow" key={d.id} data-doc={d.id}>
      <a href={open} class="docrow-main" onClick={e => { if (!open.startsWith('/f/')) { e.preventDefault(); go(open); } }} download={open.startsWith('/f/') ? d.title : undefined}>
        <span class="docrow-icon"><Icon name={icon} size={20} /></span>
        <span class="docrow-text"><span class="docrow-title">{d.title || 'Untitled'}</span>
          <span class="docrow-snippet">{d.kind === 'file' ? size(d.words || 0) : d.snippet || 'Empty'}</span></span>
        <span class="docrow-meta"><span>{timeAgo(d.updatedAt)}</span></span>
      </a>
      {d.kind === 'file' && <IconButton icon="trash" label="Delete" onClick={async () => { if (await confirmDialog({ title: `Delete ${d.title}?`, text: 'It goes to the trash for 30 days.', ok: 'Delete', danger: true })) trashDoc(d.id); }} />}
    </li>
  );
  return (
    <div class={`home ${over ? 'drop-on' : ''}`}
      onDragOver={e => { if (e.dataTransfer.types.includes('Files')) { e.preventDefault(); setOver(true); } }}
      onDragLeave={() => setOver(false)}
      onDrop={e => { if (e.dataTransfer.files.length) { e.preventDefault(); setOver(false); upload([...e.dataTransfer.files]); } }}>
      <TopBar title="Drive" />
      <div class="with-notes">
      <main id="main" class="home-main">
        <div class="list-head">
          <h2 class="section-title">Everything you have</h2>
          <span class="hint-inline">{size(used)} of 200 MB used</span>
          <input type="file" multiple hidden ref={input} onChange={e => { upload([...e.target.files]); e.target.value = ''; }} />
          <Button kind="primary" size="sm" icon="upload" onClick={() => input.current.click()}>Upload</Button>
        </div>
        <p class="hint">Drop files anywhere here to upload them (code, pictures, anything up to 20 MB each). They download back exactly as they went in.</p>
        {canSync() && (
          <div class="note drive-sync">
            <Icon name="computer" size={18} />
            <span>{folder ? <>Syncing with the folder <b>{folder.name}</b> on this computer while Drive is open. {last}</> : 'Keep a folder on this computer in step with Drive: files go both ways, and your docs are saved there as .md files.'}</span>
            <span class="grow" />
            <Button size="sm" onClick={async () => { try { const d = await pickFolder(); setFolder(d); setLast('Syncing…'); const r = await syncOnce(); setLast(`Synced · ${r?.up || 0} up, ${r?.down || 0} down, ${r?.docs || 0} docs`); } catch (err) { if (err.name !== 'AbortError') toast(err, { kind: 'error' }); } }}>{folder ? 'Change folder' : 'Sync a folder'}</Button>
          </div>
        )}
        {groups.value.length > 0 && <><h3 class="drive-h">Groups</h3><ul class="doclist">{groups.value.map(g => (
          <li class="docrow" key={g.id}><a href="/" class="docrow-main" onClick={e => { e.preventDefault(); go('/'); }}><span class="docrow-icon"><Icon name="folder" size={20} /></span>
            <span class="docrow-text"><span class="docrow-title">{g.name}</span><span class="docrow-snippet">{all.filter(d => d.groupId === g.id).length} items</span></span></a></li>))}</ul></>}
        <h3 class="drive-h">Files</h3>
        {files.length ? <ul class="doclist">{files.map(d => row(d, 'file', d.snippet?.startsWith('/f/') ? d.snippet : `/d/${d.id}`))}</ul> : <p class="hint">No files yet.</p>}
        <h3 class="drive-h">Docs</h3>
        <ul class="doclist">{docsList.map(d => row(d, 'doc', `/d/${d.id}`))}</ul>
      </main>
      <NotesSide />
      </div>
    </div>
  );
}
